// ** react imports
import React from 'react';

// ** react router imports
import { useNavigate } from 'react-router-dom';

// ** validation imports
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

// ** custom hooks imports
import { useCreateNotification } from '../../../hooks/apis/useNotification';

// ** components imports
import LoadingButton from '../../../components/common/Buttons/LoadingButton';
import NormalButton from '../../../components/common/Buttons/NormalButton';
import NormalInput from '../../../components/common/Inputs/NormalInput';
import SelectInput from '../../../components/common/Inputs/SelectInput';
import Textarea from '../../../components/common/Inputs/Textarea';
import DynamicKeyValueInput from '../../../partials/DynamicKeyValueInput';

// ** enums imports
import { BUTTON_THEMES } from '../../../enums/common';

const RenderFormSection = props => {
  const { children } = props;

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-6 overflow-hidden">
      <div className="p-4">{children}</div>
    </div>
  );
};

const AddNotification = () => {
  // hooks
  const navigate = useNavigate();

  // validation schema
  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .required('Title is required')
      .max(200, 'Title must be at most 200 characters')
      .trim(),
    content: yup.string().required('Content is required').trim(),
    category: yup
      .string()
      .nullable()
      .oneOf(
        [
          'info',
          'warning',
          'error',
          'success',
          'maintenance',
          'general',
          'alert',
          'update',
        ],
        'Category must be one of the allowed values'
      ),
    priority: yup
      .string()
      .nullable()
      .oneOf(
        ['low', 'medium', 'high', 'critical'],
        'Priority must be low, medium, high, or critical'
      ),
    type: yup
      .string()
      .nullable()
      .oneOf(
        ['system', 'user', 'broadcast', 'reminder', 'alert'],
        'Type must be one of the allowed values'
      ),
    userId: yup
      .string()
      .nullable()
      .max(10, 'User ID must be at most 100 characters'),
    isRead: yup.boolean().nullable(),
    isBroadcast: yup.boolean().nullable(),
    metadata: yup.object().nullable(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
    watch,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    defaultValues: {
      title: '',
      content: '',
      category: null,
      priority: 'medium',
      type: 'system',
      userId: '',
      isRead: false,
      isBroadcast: false,
      metadata: {},
    },
  });

  const { isCreateNotificationLoading, createNotification } =
    useCreateNotification();

  const onSubmit = async data => {
    try {
      const response = await createNotification(data);

      if (response.data?.success) {
        reset();
        setTimeout(() => {
          navigate('/notifications/list');
        }, 150);
      } else if (response.error) {
        console.error('Error creating notification:', response.error);
      }
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  const handleCancel = () => {
    navigate('/notifications/list');
  };

  const renderErrorMessage = error => {
    if (!error) return null;
    return <p className="mt-2 text-xs text-red-50">{error}</p>;
  };

  // Watch values for conditional rendering
  const isBroadcast = watch('isBroadcast');

  return (
    <div className="flex">
      <main className="grow">
        <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-[96rem] mx-auto">
          {/* Page header */}
          <div className="sm:flex sm:justify-between sm:items-center mb-6">
            {/* Left: Title */}
            <div className="mb-4 sm:mb-0">
              <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold">
                Notification
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Create a new notification with all required details
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="bg-white dark:bg-gray-800 shadow-xs rounded-xl mb-8">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="p-6 space-y-6">
                {/* Primary Information Section */}
                <RenderFormSection>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <NormalInput
                        label="Title"
                        type="text"
                        name="title"
                        register={register}
                        errors={errors.title?.message}
                        required
                        maxLength={200}
                        placeholder="Enter notification title"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <Textarea
                        label="Content"
                        name="content"
                        register={register}
                        errors={errors.content?.message}
                        required
                        placeholder="Enter notification content"
                        rows={4}
                      />
                    </div>

                    <div>
                      <SelectInput
                        label="Priority"
                        name="priority"
                        options={[
                          { label: 'Low', value: 'low' },
                          { label: 'Medium', value: 'medium' },
                          { label: 'High', value: 'high' },
                          { label: 'Critical', value: 'critical' },
                        ]}
                        register={register}
                        errors={errors.priority?.message}
                      />
                    </div>

                    <div>
                      <SelectInput
                        label="Type"
                        name="type"
                        options={[
                          { label: 'System', value: 'system' },
                          { label: 'User', value: 'user' },
                          { label: 'Broadcast', value: 'broadcast' },
                          { label: 'Reminder', value: 'reminder' },
                          { label: 'Alert', value: 'alert' },
                        ]}
                        register={register}
                        errors={errors.type?.message}
                      />
                    </div>
                  </div>
                </RenderFormSection>

                {/* Advanced Settings Section */}
                <RenderFormSection>
                  <div className="space-y-1">
                    <label
                      htmlFor="metadata"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      Metadata
                    </label>
                    <Controller
                      name="metadata"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <DynamicKeyValueInput
                          value={value ? value : {}}
                          onChange={onChange}
                          placeholder="Enter metadata as key-value pairs"
                          error={errors.metadata?.message}
                        />
                      )}
                    />
                  </div>
                </RenderFormSection>

                {/* Form Footer */}
                <footer>
                  <div className="flex flex-col px-6 py-5 border-t border-gray-200 dark:border-gray-700/60">
                    <div className="flex self-end gap-3">
                      <NormalButton
                        theme={BUTTON_THEMES.DANGER}
                        onClick={() => handleCancel()}
                      >
                        Cancel
                      </NormalButton>
                      <LoadingButton
                        type="submit"
                        theme={BUTTON_THEMES.PRIMARY}
                        isLoading={isCreateNotificationLoading}
                      >
                        Create Notification
                      </LoadingButton>
                    </div>
                  </div>
                </footer>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddNotification;
